import { AxiosResponse } from "axios";
import { isNumber } from "is-number-ts";
import moment from "moment";
import { environments, socketToken } from "../environments";
import { isMunicipio, MUNICIPIOS_EDIFICACOES_HIERARQUICAS } from "../constants/municipios";
import {
  AnyData,
  CamposDeValorUnico,
  DesmembrarUnidade,
  Foto360,
  GenericObject,
  IData,
  IFormularios,
  ILoginResponse,
  IRelease,
  ImportacoesTiff,
  ImobiliarioEdificacao,
  PermissoesProjetos,
  Tabelas,
  SituacaoIptuPayload,
  SinterAuditoriaResponse,
} from "../interfaces";
import { apiCadastro } from "../repositories/api.cadastro";
import { apiGateway } from "../repositories/api.gateway";
import { apiPlataforma } from "../repositories/api.plataforma";
import { normalizeFormulario, TABELAS_FORMULARIOS } from "../utils/formularios";
import { filtrarConflitosNumeroCadastroPermitidosRioBranco } from "../utils/vincularEdificacaoRioBranco";
import pj from "./../../package.json";
import { getMunicipio, verifyIfInscricaoOrNumeroCadastroAlreadyExistsOnLocalDatabase } from "./data";
import { getOne } from "./db";
import { getExtraFilter, processarNovosImoveis } from "./utils";
import { processarIntegracaoSocket } from "./socket";
import { hasIntegracaoSinter } from "../utils/integracaoSinter";

export type LookupFilter = {
  column: string;
  op?: "eq" | "ilike" | "is_true" | "is_false";
  value?: any;
};

type LookupQuery = {
  key?: string;
  table: string;
  columns: string[];
  limit?: number;
  offset?: number;
  filters?: LookupFilter[];
  currentValue?: string | number;
  orderBy?: string;
  search?: string;
  searchColumns?: string[];
  selectAll?: boolean;
};

type LookupBatchResponse = {
  results: Array<{ key: string; rows: AnyData[] }>;
};

async function getProjetoIcadPadrao(): Promise<GenericObject | undefined> {
  const { data } = await apiPlataforma.get<
    GenericObject[] | { projetos?: GenericObject[]; data?: GenericObject[] }
  >("/projetos/listar?ativo=TODOS&noExtras=true&noForms=true&tipoProjeto=ICADPADRAO");

  if (Array.isArray(data)) return data[0];
  if (Array.isArray(data?.projetos)) return data.projetos[0];
  if (Array.isArray(data?.data)) return data.data[0];
  return undefined;
}

const BATCH_LOOKUP_WINDOW_MS = 40;
let lookupBatchTimer: ReturnType<typeof setTimeout> | null = null;
let lookupBatchQueue: Array<{ owner: number; key: string; query: LookupQuery }> = [];
let lookupBatchWaiters: Array<{
  owner: number;
  resolve: (value: Record<string, AnyData[]>) => void;
  reject: (reason?: unknown) => void;
}> = [];
let lookupBatchOwnerSeq = 0;

async function flushLookupBatchQueue() {
  const queue = lookupBatchQueue;
  const waiters = lookupBatchWaiters;
  lookupBatchQueue = [];
  lookupBatchWaiters = [];
  lookupBatchTimer = null;

  if (!queue.length) {
    waiters.forEach((w) => w.resolve({}));
    return;
  }

  const payload = queue.map((item, idx) => ({
    ...item.query,
    key: item.query.key || item.query.table || `q${idx}`,
  }));

  try {
    const municipio = getMunicipio();
    const { data } = await apiCadastro.post<LookupBatchResponse>(`/caracterizacao/${municipio}/lookups/batch`, {
      queries: payload,
    });

    const rowsByOwner: Record<number, Record<string, AnyData[]>> = {};
    payload.forEach((q, idx) => {
      const resItem = data?.results?.[idx];
      const rows = resItem?.rows || [];
      const key = q.key || q.table || `q${idx}`;
      const owner = queue[idx].owner;
      if (!rowsByOwner[owner]) rowsByOwner[owner] = {};
      rowsByOwner[owner][key] = rows;
    });

    waiters.forEach((w) => w.resolve(rowsByOwner[w.owner] || {}));
  } catch (err) {
    waiters.forEach((w) => w.reject(err));
  }
}

async function fetchLookupBatch(queries: LookupQuery[]): Promise<Record<string, AnyData[]>> {
  const owner = ++lookupBatchOwnerSeq;
  const normalized = queries.map((q, idx) => ({
    owner,
    key: q.key || q.table || `q${idx}`,
    query: {
      ...q,
      key: q.key || q.table || `q${idx}`,
    },
  }));

  return new Promise((resolve, reject) => {
    lookupBatchQueue.push(...normalized);
    lookupBatchWaiters.push({ owner, resolve, reject });

    if (!lookupBatchTimer) {
      lookupBatchTimer = setTimeout(flushLookupBatchQueue, BATCH_LOOKUP_WINDOW_MS);
    }
  });
}

/**
 * Realiza uma tentativa de login na plataforma com o email e a senha fornecidos.
 *
 * @async
 * @param {string} email - O endereço de email do usuário.
 * @param {string} senha - A senha do usuário.
 * @returns {Promise<ILoginResponse | undefined>} Uma promessa que será resolvida com um objeto de resposta de login se o login for bem-sucedido, ou undefined se o login falhar.
 *
 * @example
 * const email = "user@example.com";
 * const senha = "senha123";
 *
 * try {
 *   const resultadoLogin = await login(email, senha);
 *   if (resultadoLogin) {
 *     console.log("Login bem-sucedido. Token de acesso:", resultadoLogin.token);
 *   } else {
 *     console.warn("Login falhou. Verifique suas credenciais.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao tentar fazer login:", error);
 * }
 */

export async function login(email: string, senha: string): Promise<ILoginResponse | undefined> {
  try {
    const login = await apiPlataforma.post("/login", {
      email: email,
      senha: senha,
    });

    localStorage.setItem("email", email);

    return login.data;
  } catch (error) {
    console.error("Não foi possível realizar o login", error);
  }
}

/**
 * Obtém as configurações do tenant da plataforma.
 *
 * @async
 * @returns {Promise<{ [key: string]: any } | undefined>} Uma promessa que será resolvida com um objeto contendo as configurações do tenant se a operação for bem-sucedida, ou undefined se falhar.
 *
 * @example
 * try {
 *   const configTenant = await getTenantConfig();
 *   if (configTenant) {
 *     console.log("Configurações do tenant obtidas com sucesso:", configTenant);
 *   } else {
 *     console.warn("Não foi possível obter as configurações do tenant.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao obter as configurações do tenant:", error);
 * }
 */

export async function getTenantConfig(): Promise<{ [key: string]: any } | undefined> {
  try {
    const response = await apiPlataforma.get("/tenants/config");
    let tenantConfig = JSON.parse(response.data.config);

    if (!("configCaracterizacao" in tenantConfig)) {
      tenantConfig.configCaracterizacao = {
        desabilitaLote: false,
        desabilitaImobiliario: false,
        desabilitaPessoa: false,
        desabilitaEdificacao: false,
        desabilitaVisita: false,
        desabilitaControle: false,
      };
    }

    return tenantConfig;
  } catch (error) {
    console.error("Não foi possível obter as configurações do tenant, verifique a estrutura do JSON", error);
    return undefined;
  }
}

/**
 * Obtém os dados de um recurso com base em seu ID.
 *
 * @async
 * @param {number} id - O ID do recurso a ser obtido.
 * @returns {Promise<IData | undefined>} Uma promessa que será resolvida com os dados do recurso se a operação for bem-sucedida, ou undefined se falhar.
 *
 * @example
 * const resourceId = 123;
 * try {
 *   const resourceData = await getData(resourceId);
 *   if (resourceData) {
 *     console.log("Dados do recurso obtidos com sucesso:", resourceData);
 *   } else {
 *     console.warn("Não foi possível obter os dados do recurso.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao obter os dados do recurso:", error);
 * }
 */

export async function getData(
  id: number,
  page: number = 1,
  tabela?: string,
  search?: string,
): Promise<IData | undefined> {
  try {
    let url = `/caracterizacao/${getMunicipio()}/get-data/${id}?page=${page}`;

    if (tabela) url += `&table=${tabela}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;

    const request = await apiCadastro.get(url);

    return request.data;
  } catch (error) {
    console.error("Não foi possível obter os dados do lote", error);
  }
}

/**
 * Obtém a estrutura de dados de uma tabela específica da API REST.
 *
 * @param {string} table - O nome da tabela para a qual se deseja obter a estrutura de dados.
 * @returns {Promise<IData | undefined>} - Uma Promise que resolve para a estrutura de dados da tabela ou undefined se houver um erro.
 * @throws {Error} - Lança um erro se não for possível obter os dados da tabela.
 */

export async function getDataStructureByTable(table: string): Promise<IData | undefined> {
  try {
    const structures = await getDataStructures([table]);
    return structures[table];
  } catch (error) {
    console.error(`Não foi possível obter os dados da tabela ${table}`, error);
  } 
}

export async function getDataStructures(tables: string[]): Promise<Record<string, IData | undefined>> {
  if (!tables || tables.length === 0) return {};

  try {
    const queries: LookupQuery[] = tables.map((table, idx) => ({
      key: `structure_${table}_${idx}`,
      table,
      columns: [],
      limit: 1,
      selectAll: true,
    }));

    const mapped = await fetchLookupBatch(queries);

    const result: Record<string, IData | undefined> = {};
    tables.forEach((table, idx) => {
      const key = `structure_${table}_${idx}`;
      const rows = mapped[key] || mapped[table] || [];
      result[table] = rows[0] as IData | undefined;
    });

    return result;
  } catch (error) {
    console.error("Não foi possível obter as estruturas das tabelas", error);
    return {};
  }
}

/**
 * Obtém os formulários associados a tabelas de um projeto no contexto da plataforma.
 *
 * @async
 * @returns {Promise<IFormularios | undefined>} Uma promessa que será resolvida com os formulários se a operação for bem-sucedida, ou undefined se falhar.
 *
 * @example
 * try {
 *   const forms = await getForms();
 *   if (forms) {
 *     console.log("Formulários obtidos com sucesso:", forms);
 *   } else {
 *     console.warn("Não foi possível obter os formulários.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao obter os formulários:", error);
 * }
 */

export async function getForms(): Promise<IFormularios | undefined> {
  try {
    const projetoIcad = await getProjetoIcadPadrao();
    if (!projetoIcad) {
      console.warn("Nenhum projeto ICADPADRAO encontrado.");
      return undefined;
    }

    const projetoCamadas = (await apiPlataforma.get("/projetos/" + projetoIcad.id + "?noForms=true")).data.projetosCamadas;
    const formulariosResponse = await apiPlataforma.get(`/formularios/v3?projetoId=${projetoIcad.id}`);
    const formulariosRaw: any[] = Array.isArray(formulariosResponse.data)
      ? formulariosResponse.data
      : formulariosResponse.data?.formularios || [];

    const representacaoPorCamadaId = new Map<number, Tabelas>();
    projetoCamadas.forEach((camada: any) => {
      if (TABELAS_FORMULARIOS.includes(camada?.representacao)) {
        representacaoPorCamadaId.set(Number(camada.id), camada.representacao);
      }
    });

    const formularios: IFormularios = {
      lote: [],
      imobiliario: [],
      imobiliario_edificacao: [],
      edificacao: [],
      pessoa: [],
      imobiliario_pessoa: [],
      visitas: [],
      mensagens: [],
      midia: [],
    };

    formulariosRaw.forEach((formulario) => {
      if (!formulario?.ativo) return;

      const camadaId = Number(formulario?.id_projetos_camadas ?? formulario?.idProjetosCamadas ?? NaN);
      const representacaoFromCamada = representacaoPorCamadaId.get(camadaId);
      const representacaoFromForm = formulario?.representacao;
      const representacao = representacaoFromCamada || representacaoFromForm;

      if (!TABELAS_FORMULARIOS.includes(representacao as Tabelas)) return;

      const tabela = representacao as Tabelas;
      formularios[tabela].push(normalizeFormulario(formulario, tabela));
    });

    return formularios;
  } catch (error) {
    console.error("Não foi possível obter os formulários", error);
    return undefined;
  }
}

/**
 * Obtém o ID do primeiro lote disponível na fonte de dados.
 *
 * @async
 * @returns {Promise<string | undefined>} Uma promessa que será resolvida com o ID do primeiro lote se a operação for bem-sucedida, ou undefined se falhar.
 *
 * @example
 * try {
 *   const firstLoteId = await getFirstIdLote();
 *   if (firstLoteId) {
 *     console.log("ID do primeiro lote:", firstLoteId);
 *   } else {
 *     console.warn("Não foi possível obter o ID do primeiro lote.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao obter o ID do primeiro lote:", error);
 * }
 */

export async function getFirstIdLote(): Promise<any> {
  try {
    const firstLote = await apiGateway.get("/rest/lote?limit=1");

    return firstLote.data[0].id;
  } catch (error) {
    console.error(error);
  }
}

/**
 * Obtém uma foto em 360 graus (Foto360) com base em coordenadas geográficas.
 *
 * @async
 * @param {number[]} coords - As coordenadas geográficas (latitude e longitude) para a busca da foto em 360 graus.
 * @returns {Promise<Foto360 | undefined>} Uma promessa que será resolvida com a Foto360 se a operação for bem-sucedida, ou undefined se falhar.
 *
 * @example
 * const latitude = 123.456;
 * const longitude = 789.012;
 * const coordinates = [latitude, longitude];
 *
 * try {
 *   const photoSphere = await getPhotoSphere(coordinates);
 *   if (photoSphere) {
 *     console.log("Foto em 360 graus obtida com sucesso:", photoSphere);
 *   } else {
 *     console.warn("Não foi possível obter a foto em 360 graus para as coordenadas fornecidas.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao obter a foto em 360 graus:", error);
 * }
 */

export async function getPhotoSphere(coords: number[]): Promise<Foto360 | undefined> {
  try {
    const response: { [key: string]: any } = await apiCadastro.get(
      `/caracterizacao/${localStorage.municipio}/foto_360/coords/${coords[0]}/${coords[1]}/`,
    );

    return response.data[0];
  } catch (error) {
    console.error(error);
  }
}

/**
 * Desmembra uma unidade imobiliária.
 *
 * @async
 * @param {string} idUnidade - O identificador da unidade imobiliária.
 * @param {string} nrUnidade - O número da nova unidade imobiliária.
 * @param {number} areaConstruida - A área construída equivalente em m².
 * @returns {Promise<string | undefined>} Uma promessa que será resolvida e retornará 'success' ou `undefined` caso falhe.
 *
 * @example
 * desmembrarUnidade("123", "003", 195.00)
 *   .then((resultado) => {
 *     if (resultado === 'success') {
 *       console.log('Desmembramento realizado com sucesso!');
 *     } else {
 *       console.log('Falha no desmembramento.');
 *     }
 *   });
 */

export function desmembrarUnidade(
  idUnidade: string,
  nrUnidade: string,
  areaConstruida: number,
  codigoPessoa: string,
  idPessoa: string,
): Promise<DesmembrarUnidade | undefined>;
export function desmembrarUnidade(
  idUnidade: string,
  nrUnidade: string,
  areaConstruida: number,
  codigoPessoa: string,
  idPessoa: string,
  areaTelheiro: number,
  areaGalpao: number,
): Promise<DesmembrarUnidade | undefined>;
export async function desmembrarUnidade(
  idUnidade: string,
  nrUnidade: string,
  areaConstruida: number,
  codigoPessoa: string,
  idPessoa: string,
  areaTelheiro?: number,
  areaGalpao?: number,
): Promise<DesmembrarUnidade | undefined> {
  try {
    const body: Record<string, any> = {
      nr_unidade: nrUnidade,
      area_construida_privativa: areaConstruida,
      codigo_pessoa: codigoPessoa,
      id_pessoa: idPessoa,
    };

    switch (getMunicipio()) {
      case "vitoriadaconquista":
        if (typeof areaTelheiro === "number" && typeof areaGalpao === "number") {
          body.area_telheiro = areaTelheiro;
          body.area_galpao = areaGalpao;

          const telheiroPreenchido = areaTelheiro > 0;
          const galpaoPreenchido = areaGalpao > 0;

          if (
            (areaGalpao === 0 && telheiroPreenchido) ||
            (areaTelheiro === 0 && galpaoPreenchido) ||
            (areaTelheiro === 0 && areaGalpao === 0)
          ) {
            body.ignorar_verificacoes_areas = true;
          }
        }
        break;
      default:
        break;
    }

    const response = await apiCadastro.post(`caracterizacao/${getMunicipio()}/desmembrar/unidade/${idUnidade}`, body);
    if (response?.status === 200) {
      return response.data as DesmembrarUnidade;
    } else {
      console.error("Erro na resposta:", response.status, response.statusText);
      return undefined;
    }
  } catch (error) {
    console.error("Erro ao desmembrar unidade:", error);
    return undefined;
  }
}

/**
 * Une dois imobiliários.
 *
 * @async
 * @param {string} idUnidade1 - O identificador da primeira unidade imobiliária.
 * @param {string} idUnidade2 - O identificador da segunda unidade imobiliária que será unificada à primeira.
 * @returns {Promise<string | undefined>} Uma promessa que será resolvida e retornará 'success' ou `undefined` caso falhe.
 *
 * @example
 * unirImoveis("123", "456")
 *   .then((resultado) => {
 *     if (resultado === 'success') {
 *       console.log('Unificação realizada com sucesso!');
 *     } else {
 *       console.log('Falha na unificação.');
 *     }
 *   });
 */

export async function unirImoveis(
  idUnidade1: string,
  idUnidade2: number,
  payloadExtra?: {
    vinculos_edificacao?: Array<{
      id_imobiliario_edificacao: string | number;
      id_edificacao: string | number;
      acao: string;
      id_edificacao_alvo?: string | number;
    }>;
  },
): Promise<string | undefined> {
  try {
    const response = await apiCadastro.post(`caracterizacao/${getMunicipio()}/unificar/unidade/${idUnidade1}`, {
      id_unidade_unificada: idUnidade2,
      ...payloadExtra,
    });

    if (response?.status === 200) {
      return "success";
    } else {
      console.error("Erro na resposta:", response.status, response.statusText);
      return undefined;
    }
  } catch (error) {
    console.error("Erro ao unificar lotes:", error);
    return undefined;
  }
}

/**
 * Envia logs para uma fonte de dados específica.
 *
 * @async
 * @returns {Promise<any>} Uma promessa que será resolvida com a resposta da solicitação de envio de logs, ou rejeitada com o erro, se a operação for bem-sucedida ou falhar.
 *
 * @example
 * try {
 *   const response = await sendLogs();
 *   console.log("Logs enviados com sucesso:", response);
 * } catch (error) {
 *   console.error("Erro ao enviar logs:", error);
 * }
 */


export async function sendLogs(): Promise<any> {
  const municipio = localStorage.getItem("municipio")?.toLowerCase();
  const logFromStorage = localStorage.getItem("log");
  if (!logFromStorage) return;

  const logs = JSON.parse(logFromStorage);
  logs.application = `Aplicação de Caracterização - v${pj.version} - ${environments.ambiente}`;

  try {
    const response = await apiCadastro.post(
      `/caracterizacao/${localStorage.municipio}/processar-logs`,
      logs
    );

    const novosImoveis = await processarNovosImoveis(response.data);
    response.data.novosImoveis = novosImoveis;

    if (response.status === 200 && municipio) {
      const municipiosComIntegracaoNormal = ["riobranco", "vitoriadaconquista", "maracanau"];
      const usaIntegracaoNormal = municipiosComIntegracaoNormal.includes(municipio);
      const usaIntegracaoSinter = usaIntegracaoNormal
        ? false
        : hasIntegracaoSinter(await getTenantConfig());

      if (usaIntegracaoNormal || usaIntegracaoSinter) {
        const insertsIds = response.data.inserts_ids || [];
        const containsUUID = logs.inserts.some(
          (entry: any) => typeof entry.fid === "string" && entry.fid.includes("-")
        );

        if (containsUUID) {
          logs.inserts = insertsIds;
        }

        await processarIntegracaoSocket(municipio, logs, {
          sinterOnly: !usaIntegracaoNormal,
        });
      }
    }

    return response.data;
  } catch (error) {
    console.error("Erro ao enviar logs:", error);
    return error;
  }
}

/**
 * Envia um arquivo para armazenamento em uma fonte de dados específica.
 *
 * @async
 * @param {string} nome - O nome do arquivo.
 * @param {string} arquivo - O conteúdo do arquivo em formato base64.
 * @param {string} extensao - A extensão do arquivo.
 * @returns {Promise<any>} Uma promessa que será resolvida com a resposta da solicitação de envio de arquivo, ou rejeitada com o erro, se a operação for bem-sucedida ou falhar.
 *
 * @example
 * const fileName = "example.jpg";
 * const fileContent = "base64-encoded-content";
 * const fileExtension = "jpg";
 *
 * try {
 *   const response = await sendFile(fileName, fileContent, fileExtension);
 *   console.log("Arquivo enviado com sucesso:", response);
 * } catch (error) {
 *   console.error("Erro ao enviar arquivo:", error);
 * }
 */

export async function sendFile(nome: string, arquivo: string, extensao: string): Promise<AxiosResponse<any, any>> {
  try {
    return await apiCadastro.post(`/midia/${getMunicipio()}/storage`, {
      nome: nome,
      arquivo: arquivo,
      extensao: extensao,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Obtém informações sobre as importações de arquivos TIFF com base em filtros específicos.
 *
 * @async
 * @returns {Promise<ImportacoesTiff[]>} Uma promessa que será resolvida com um array de objetos ImportacoesTiff se a operação for bem-sucedida.
 *
 * @example
 * try {
 *   const importacoes = await getImportacoesTiff();
 *   console.log("Importações de arquivos TIFF obtidas com sucesso:", importacoes);
 * } catch (error) {
 *   console.error("Erro ao obter informações sobre as importações de arquivos TIFF:", error);
 * }
 */

export async function getImportacoesTiff(): Promise<ImportacoesTiff[]> {
  try {
    const request = await apiPlataforma.get("/importacoes-tiff?filtros=representacao,eq,BaseMapLocal");

    return request.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Envia feedback de sincronização para o sistema de rastreamento de problemas (Easy Redmine).
 * @param {IObjectError[]} errosLog - Os erros de log.
 * @returns {Promise<IIssueOnEasyRedmine>} Uma Promise que resolve quando o feedback é enviado com sucesso.
 * @throws {Error} Lança um erro se houver um problema ao enviar o feedback.
 */

export async function sendFeedbackSincronizacao(errosLog: any): Promise<any> {
  const ambiente = environments.ambiente;
  const urlApiEspecifica = environments.urlApiCadastro;
  const urlApiGateway = environments.urlApiGateway;
  const urlApiGenerica = environments.urlApiPlataforma;
  const versao = pj.version;
  const userAuthToken = localStorage.xAuthToken;
  const tenantToken = localStorage.xTnToken;
  const nomeMunicipio = getMunicipio();
  const logData = localStorage.getItem("log") || "{}";
  const email = localStorage.email;

  const body = {
    issue: {
      subject: `Relatório de Erros - Caracterização - ${nomeMunicipio}- ${versao}`,
      description: `Detalhes:\n
                    Versão da aplicação: ${versao} - ${ambiente}\n
                    URL da API Específica: ${urlApiEspecifica}\n
                    URL da API Gateway: ${urlApiGateway}\n
                    URL da API Genérica: ${urlApiGenerica}\n
                    Municipio: ${nomeMunicipio}\n
                    Token do Usuário: ${userAuthToken}\n
                    Token do Tenant: ${tenantToken}\n
                    Data e hora do erro: ${moment().format("DD MMMM YYYY, h:mm:ss a")}`,
      easy_email_to: email,
      easy_email_cc: email,
      project_id: 185,
      tracker_id: 12,
      status_id: 2,
      author_id: 4,
      priority_id: 12,
      custom_fields: [
        {
          id: 121,
          value: logData,
        },
        {
          id: 122,
          value: JSON.stringify(errosLog),
        },
        {
          id: 123,
          value: `Bearer ${userAuthToken}`,
        },
      ],
    },
  };
  try {
    return await apiCadastro.post("/caracterizacao/criar-issue-easyredmine", body);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Obtém os logradouros mais próximos a um lote com base em um ID de lote específico.
 *
 * @async
 * @param {string} id_lote - O ID do lote para o qual você deseja encontrar logradouros próximos.
 * @returns {Promise<AnyData[]>} Uma promessa que será resolvida com um array de objetos AnyData representando os logradouros mais próximos se a operação for bem-sucedida.
 *
 * @example
 * const loteId = "12345"; // Substitua com o ID do lote desejado
 *
 * try {
 *   const logradourosProximos = await getLogradouroMaisProximo(loteId);
 *   console.log("Logradouros mais próximos obtidos com sucesso:", logradourosProximos);
 * } catch (error) {
 *   console.error("Erro ao obter os logradouros mais próximos:", error);
 * }
 */

export async function getLogradouroMaisProximo(id_lote: string): Promise<AnyData> {
  try {
    const request = await apiCadastro.get(
      `/caracterizacao/${getMunicipio()}/nearest-features/lote/${id_lote}/logradouro/10`,
    );

    return request.data[0];
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Busca opções de dados da API com base no tipo e nome fornecidos.
 *
 * @param {string} type - O tipo de recurso a ser consultado na API.
 * @param {string} name - O nome do campo a ser selecionado na resposta da API.
 * @returns {Promise<AnyData[]>} - Uma promessa que resolve para um array de dados de opções.
 * @throws {Error} - Lança um erro se a requisição à API falhar.
 */
export async function getDescriptions(
  type: string,
  name: string[],
  currentValue?: string | number,
  filters: LookupFilter[] = [],
): Promise<AnyData[]> {
  try {
    const columns = Array.from(new Set(name.filter((c) => !!c && typeof c === "string")));
    const mapped = await fetchLookupBatch([
      {
        key: "default",
        table: type,
        columns,
        limit: 10,
        filters,
        currentValue,
      },
    ]);

    return mapped.default || mapped[type] || [];
  } catch (error) {
    throw error;
  }
}

/**
 * Busca opções de dados da API com base no tipo e valor do ID fornecidos.
 *
 * @param {string} type - O tipo de recurso a ser consultado na API.
 * @param {string} value - O valor do ID a ser filtrado na consulta.
 * @returns {Promise<AnyData[]>} - Uma promessa que resolve para um array de dados de opções.
 * @throws {Error} - Lança um erro se a requisição à API falhar.
 */
export async function getDescriptionsById(type: string, value: string): Promise<AnyData[]> {
  try {
    const mapped = await fetchLookupBatch([
      {
        key: "byId",
        table: type,
        columns: ["id"],
        limit: 1,
        filters: [{ column: "id", op: "eq", value }],
      },
    ]);
    return mapped.byId || mapped[type] || [];
  } catch (error) {
    throw error;
  }
}

/**
 * Busca opções de dados da API com base no tipo, valor do nome e nome do campo fornecidos.
 *
 * @param {string} table - O tipo de recurso a ser consultado na API.
 * @param {string} value - O valor do nome a ser filtrado na consulta.
 * @param {string[]} name - O nome do campo a ser selecionado na resposta da API.
 * @returns {Promise<AnyData[]>} - Uma promessa que resolve para um array de dados de opções.
 * @throws {Error} - Lança um erro se a requisição à API falhar.
 */
export async function getDescriptionsByName(
  table: string,
  value: string | undefined,
  columns: string[],
  filters: LookupFilter[] = [],
  orderby: string,
  offset: number = 0,
): Promise<AnyData[]> {
  try {
    const saneColumns = (columns || []).map((c) => (c ?? "").trim()).filter((c) => c.length > 0);

    const mapped = await fetchLookupBatch([
      {
        key: "search",
        table,
        columns: saneColumns,
        limit: 10,
        offset,
        filters,
        orderBy: orderby,
        search: value,
        searchColumns: saneColumns,
      },
    ]);

    return mapped.search || mapped[table] || [];
  } catch (error) {
    throw error;
  }
}

/**
 * Verifica se uma inscrição cartográfica ou um número cadastro específicos já existem na tabela especificada.
 *
 * @param {string} table - A tabela onde o campo será procurado.
 * @param {string} id - O valor a ser comparado na busca da inscrição cartográfica.
 * @param {CamposDeValorUnico} campo - O nome do campo pelo qual a busca será realizada.
 * @param {string} currentId - O ID atual da feição para ser excluído da busca.
 * @returns {Promise<boolean>} - Uma Promise que resolve para true se a inscrição cartográfica existir, ou false se não existir ou houver um erro.
 * @throws {Error} - Lança um erro se ocorrer um problema ao realizar a busca.
 */
export async function verifyIfInscricaoOrNumeroCadastroAlreadyExists(
  table: Tabelas,
  value: string,
  campo: CamposDeValorUnico,
  currentId: string,
  context?: { idImobiliario?: string },
): Promise<boolean> {
  try {
    const municipio = localStorage.getItem("municipio");
    const extraFilter = getExtraFilter(municipio, table);

    let existsOnRemote: boolean;
    if (isNumber(currentId)) {
      const { data } = await apiGateway.get(`/rest/${table}?${campo}=eq.${value}${extraFilter}&id=neq.${currentId}`);
      let remoteData = data as ImobiliarioEdificacao[];

      if (
        table === "imobiliario_edificacao" &&
        campo === "numero_cadastro" &&
        isMunicipio(municipio, MUNICIPIOS_EDIFICACOES_HIERARQUICAS)
      ) {
        const vinculoAtual = getOne(currentId, table) as ImobiliarioEdificacao | undefined;
        if (vinculoAtual) {
          remoteData = filtrarConflitosNumeroCadastroPermitidosRioBranco(
            vinculoAtual,
            remoteData,
            context?.idImobiliario ?? vinculoAtual.id_imobiliario,
          );
        }
      }

      existsOnRemote = remoteData.length > 0;
    } else {
      const { data } = await apiGateway.get(`/rest/${table}?${campo}=eq.${value}${extraFilter}`);
      existsOnRemote = data.length > 0;
    }

    const existsOnLocalDatabase = verifyIfInscricaoOrNumeroCadastroAlreadyExistsOnLocalDatabase(
      table,
      value,
      campo,
      currentId,
      context,
    );

    return existsOnRemote || existsOnLocalDatabase;
  } catch (error) {
    console.warn(error);
    return false;
  }
}

/**
 * Obtém as releases do GitHub da aplicação.
 *
 * @throws {Error} Lança um erro se a requisição falhar.
 * @returns {Promise<IRelease[]>} Uma Promise que resolve para um array de objetos IRelease.
 */
export async function getGithubReleases(): Promise<IRelease[]> {
  try {
    const response = await apiCadastro.get(`/caracterizacao/github-releases`);
    return response.data;
  } catch (error) {
    console.error("Não foi possível obter as releases", error);
    throw error;
  }
}

/**
 * Busca as permissões do projeto na API Plataforma para o usuario autenticado.
 * @async
 * @function getPermissoesProjetos
 * @returns {Promise<PermissoesProjetos | undefined>} Uma promessa que resolve para as permissões do projeto ou indefinido se ocorrer um erro.
 * @throws Lançará um erro se a solicitação da API falhar.
 */
export async function getPermissoesProjetos(): Promise<PermissoesProjetos | undefined> {
  try {
    const projetoIcad = await getProjetoIcadPadrao();
    if (!projetoIcad) {
      throw new Error("Projeto ICADPADRAO não encontrado");
    }

    const { data: permissoes } = await apiPlataforma.get<PermissoesProjetos>(`/permissoes/projetos/${projetoIcad.id}`);

    return permissoes;
  } catch (error) {
    console.error("Não foi possível obter as permissões do projeto", error);
    return undefined;
  }
}

/**
 * Atualiza os dados da pessoa com as informações tributárias.
 *
 * Essa função realiza uma requisição POST para o webhook de atualização
 * de pessoa, enviando o ID da pessoa (obtido no contexto dos dados tributários)
 * para atualizar as informações no sistema. Atualmente sendo usado somente em Maracanaú.
 *
 * @async
 * @param {number | string} pessoaId - O ID da pessoa a ser atualizado com os dados tributários.
 * @returns {Promise<any | undefined>} Uma promise que será resolvida com os dados da resposta se a operação for bem-sucedida,
 * ou undefined em caso de falha.
 *
 * @example
 * const pessoaId = 123;
 * try {
 *   const response = await webhookAtualizaDados("pessoa", { id: pessoaId });
 *   if (response) {
 *     console.log("Pessoa atualizada com sucesso:", response);
 *   } else {
 *     console.warn("Não foi possível atualizar a pessoa.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao atualizar a pessoa:", error);
 * }
 */
export async function webhookAtualizaDados(camada: string, payload: {}): Promise<any | undefined> {
  try {
    const municipio = getMunicipio();
    const url = `${environments.urlApiWorkflowIntegracao}/${municipio}-${camada}`;
    const token = localStorage.getItem("xAuthToken");
    const isVitoriaDaConquista = municipio?.toLowerCase() === "vitoriadaconquista";

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: isVitoriaDaConquista ? socketToken || "" : `Bearer ${token}`,
    };

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Erro ao atualizar a pessoa. Código: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Erro ao atualizar a pessoa:", error);
    return undefined;
  }
}

/**
 * Consulta os dados da pessoa com as informações tributárias.
 *
 * Essa função realiza uma requisição POST para o webhook de atualização
 * de pessoa, enviando o ID da pessoa (obtido no contexto dos dados tributários)
 * para atualizar as informações no sistema. Atualmente sendo usado somente em Maracanaú.
 *
 * @async
 * @param {number | string} pessoaId - O ID da pessoa a ser consultado com os dados tributários.
 * @returns {Promise<any | undefined>} Uma promise que será resolvida com os dados da pessoa
 *
 * @example
 * const pessoaId = 123;
 * try {
 *   const response = await consultaWebhook("pessoa", { codigo: codigoPessoa });
 *   if (response) {
 *     console.log("Pessoa atualizada com sucesso:", response);
 *   } else {
 *     console.warn("Não foi possível atualizar a pessoa.");
 *   }
 * } catch (error) {
 *   console.error("Erro ao atualizar a pessoa:", error);
 * }
 */
export async function consultaWebhook(camada: string, payload: {}): Promise<any | undefined> {
  try {
    const url = `${environments.urlApiWorkflowIntegracao}/${getMunicipio()}-consulta-${camada}`;
    const token = localStorage.getItem("xAuthToken");

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.status || response.status !== 200) {
      throw new Error(`Erro ao consultar pessoa. Código: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Erro ao atualizar a pessoa:", error);
    return undefined;
  }
}

/**
 * Realiza o vínculo de uma pessoa a múltiplos imóveis (imobiliários) de um determinado lote.
 *
 * Essa função envia uma requisição POST para o endpoint de vinculação múltipla de imóveis
 * no sistema para Maracanaú. É utilizada para associar rapidamente uma pessoa
 * (representada por `id_pessoa`) a um ou mais imóveis (`ids_imobiliarios`) dentro de um lote específico (`id_lote`).
 *
 *
 * @async
 * @param {Object} payload - Os dados necessários para realizar o vínculo.
 * @param {number} payload.id_pessoa - O ID da pessoa que será vinculada.
 * @param {number} payload.id_lote - O ID do lote ao qual os imóveis pertencem.
 * @param {(string | number)[]} payload.ids_imobiliarios - Um array com os IDs dos imóveis a serem vinculados. Pode conter o valor especial `"__allImobiliarios__"` para indicar todos os imóveis do lote.
 * @returns {Promise<any>} Uma promise com a resposta da API, caso o vínculo seja realizado com sucesso.
 *
 * @throws {Error} Lança um erro caso a requisição falhe ou o servidor retorne erro.
 *
 * @example
 * const payload = {
 *   id_pessoa: 42,
 *   id_lote: 1023,
 *   ids_imobiliarios: [123, 124, 125]
 * };
 *
 * try {
 *   const result = await vincularMultiImobiliarios(payload);
 *   console.log("Vínculo realizado:", result);
 * } catch (error) {
 *   console.error("Falha ao vincular imóveis:", error);
 * }
 */
export async function getVinculosImobiliarioEdificacaoPorLote(idLote: number) {
  try {
    const response = await apiCadastro.get(`/${getMunicipio()}/lote/${idLote}/imobiliario-edificacao/`);
    return (response.data?.vinculos || []) as AnyData[];
  } catch (error) {
    console.error("Erro ao buscar vínculos imobiliário-edificação do lote:", error);
    return [];
  }
}

export async function vincularMultiImobiliarios(payload: {
  id_pessoa: number;
  id_lote: number;
  ids_imobiliarios: (string | number)[];
}) {
  try {
    const token = localStorage.getItem("xAuthToken");

    const response = await apiCadastro.post(`/caracterizacao/${getMunicipio()}/imobiliario_pessoa/vincular-multi/`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Erro ao vincular múltiplos imobiliários:", error);
    throw error;
  }
}
/**
 * Obtém situação de IPTU (POST ⟶ apiCadastro)
 * - devolve sempre um array (vazio se não achou nada)
 */
export async function getSituacaoIptu({ municipio, id_imobiliario = [], id_lote = [] }: SituacaoIptuPayload) {
  const payload: Record<string, unknown> = {};
  if (id_imobiliario.length) payload.id_imobiliario = id_imobiliario;
  if (id_lote.length) payload.id_lote = id_lote;

  const { data } = await apiCadastro.post(`/crud/${municipio}/situacao_iptu`, payload);
  return data as {
    id_imobiliario: number | null;
    id_lote: number | null;
    situacao_iptu: string;
  }[];
}

export async function getAuditarUsuarios({ id_tenants }: { id_tenants: number }) {
  try {
    const { data } = await apiCadastro.get(`caracterizacao/get_auditar_usuarios/${id_tenants}`);
    return data;
  } catch (error) {
    console.error("Erro ao obter os dados de auditoria dos usuários:", error);
    throw error;
  }
}

export async function getIdTenantConfig(): Promise<number|undefined> {
  try {
    const response = await apiPlataforma.get("/tenants/config");
    return response.data.id_tenants;
  } catch (error) {
    return undefined;
  }
}

export async function getSinterAuditoria(
  page = 1,
  pageSize = 20,
): Promise<SinterAuditoriaResponse | undefined> {
  try {
    const municipio = getMunicipio();
    const { data } = await apiCadastro.get<SinterAuditoriaResponse>(
      `/${municipio}/sinter/auditoria`,
      { params: { page, page_size: pageSize } },
    );
    return data;
  } catch (error) {
    console.error("Erro ao buscar auditoria SINTER:", error);
    return undefined;
  }
}

export async function retrySinterIntegracao(idAuditIntegracao: number): Promise<boolean> {
  try {
    const municipio = getMunicipio();
    const { status } = await apiCadastro.post(
      `/${municipio}/sinter/auditoria/${idAuditIntegracao}/retry`,
    );
    return status >= 200 && status < 300;
  } catch (error) {
    console.error("Erro ao reprocessar integração SINTER:", error);
    return false;
  }
}
